import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ReactComponent as ProfileManagementIcon } from "../../../../assets/icons/sidebar-profile-management.svg";
import { ReactComponent as IpCategoryIcon } from "../../../../assets/icons/sidebar-ip-category.svg";
import { ReactComponent as DocumentsIcon } from "../../../../assets/icons/sidebar-documents.svg";
import { ReactComponent as ProcedureManagementIcon } from "../../../../assets/icons/sidebar-procedure-management.svg";
import { ReactComponent as ApprovalManagementIcon } from "../../../../assets/icons/sidebar-approval-management.svg";
import { ReactComponent as OrderManagementIcon } from "../../../../assets/icons/sidebar-order-management.svg";
import { ReactComponent as IpMarketplaceIcon } from "../../../../assets/icons/sidebar-ip-marketplace.svg";
import { ReactComponent as AccountManagementIcon } from "../../../../assets/icons/sidebar-account-management.svg";
import { ReactComponent as StatisticsIcon } from "../../../../assets/icons/sidebar-statistics.svg";
import { ReactComponent as PolicyIcon } from "../../../../assets/icons/sidebar-policy.svg";
import { ReactComponent as QuotationIcon } from "../../../../assets/icons/sidebar-quotation.svg";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};
const panel = {
  hidden: { x: "-100%" },
  show: { x: 0, transition: { type: "spring", stiffness: 380, damping: 36 } },
  exit: { x: "-100%", transition: { duration: 0.18 } },
};

const SideMenu = ({ isOpen, onClose, user }) => {
  const location = useLocation();

  // Esc to close + lock scroll
  const onKey = useCallback(
    (e) => e.key === "Escape" && onClose?.(),
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onKey]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.button
            aria-label="Đóng menu"
            className="fixed inset-0 z-[100] bg-black/50"
            onClick={onClose}
            variants={backdrop}
            initial="hidden"
            animate="show"
            exit="exit"
          />
          <motion.div
            className="fixed left-0 top-0 z-[101] h-full w-[75%] max-w-[320px] overflow-hidden bg-white shadow-2xl"
            variants={panel}
            initial="hidden"
            animate="show"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 flex h-full w-full flex-col">
              {/* Header */}
              <div className="relative flex min-h-[195px] flex-col items-center overflow-hidden bg-[#0155F8] px-4 pb-6 pt-7 text-white">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -right-16 -top-16 size-40 rounded-full border-[26px] border-white/10" />
                  <div className="absolute -left-9 -top-8 h-20 w-20 rounded-full bg-white/10" />
                  <div className="absolute left-[63%] top-6 size-5 rounded-full border-[5px] border-white/10" />
                  <div className="absolute -left-12 bottom-6 size-24 rounded-full border-[24px] border-white/10" />
                </div>
                <img
                  src={user.avatar || "/images/avatar-default.png"}
                  alt=""
                  className="relative z-10 size-[84px] rounded-full border-2 border-[#FFDC13] bg-white object-cover shadow-lg"
                />
                <p className="relative z-10 mt-3 text-[14px] font-sf-medium">
                  Xin chào!
                </p>
                <h2 className="relative z-10 max-w-full truncate text-[18px] font-sf-semibold text-[#FFDC13]">
                  {user.fullName}
                </h2>
              </div>

              {/* Menu Items */}
              <nav className="flex-grow overflow-y-auto bg-white">
                <ul className="space-y-2 px-5 py-4">
                  <MenuItem
                    icon="/icons/home.svg"
                    label="Trang chủ"
                    link="/trang-chu"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<ProfileManagementIcon />}
                    label="Quản lý hồ sơ"
                    link="/quan-ly-ho-so"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<IpCategoryIcon />}
                    label="Danh mục tài sản trí tuệ"
                    link="/danh-muc-tai-san-tri-tue"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<DocumentsIcon />}
                    label="Tài liệu"
                    link="/tai-lieu"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<ProcedureManagementIcon />}
                    label="Hướng dẫn thủ tục"
                    link="/huong-dan-thu-tuc"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<ApprovalManagementIcon />}
                    label="Danh sách yêu cầu"
                    link="/danh-sach-yeu-cau"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<OrderManagementIcon />}
                    label="Danh sách hợp đồng"
                    link="/danh-sach-hop-dong"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<QuotationIcon />}
                    label="Báo phí"
                    link="/bao-phi"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<IpMarketplaceIcon />}
                    label="Mua bán sở hữu trí tuệ"
                    link="/mua-ban-so-huu-tri-tue"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<PolicyIcon />}
                    label="Chính sách"
                    link="/chinh-sach"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<AccountManagementIcon />}
                    label="Quản lý tài khoản"
                    link="/tai-khoan"
                    currentPath={location.pathname}
                  />
                  <MenuItem
                    icon={<StatisticsIcon />}
                    label="Báo cáo thống kê"
                    link="/bao-cao-thong-ke"
                    currentPath={location.pathname}
                  />
                </ul>
              </nav>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const MenuItem = ({ icon, label, link, external, currentPath }) => {
  const isActive =
    currentPath === link ||
    (link !== "/trang-chu" && currentPath.startsWith(`${link}/`));

  return (
    <li>
      <a
        href={link}
        className={[
          "flex min-h-[36px] items-center gap-3 rounded-lg px-1 text-[14px] transition-colors",
          isActive
            ? "bg-[#F1F6FF] font-sf-semibold text-[#0155F8]"
            : "text-[#30394A] hover:bg-[#F6FAFF] hover:text-[#0155F8]",
        ].join(" ")}
        target={external ? "_blank" : "_self"}
        rel="noopener noreferrer"
      >
        <span
          className={[
            "flex size-7 shrink-0 items-center justify-center",
            isActive ? "text-[#0155F8]" : "text-[#4B5565]",
          ].join(" ")}
        >
          {typeof icon === "string" ? (
            <img
              src={icon}
              alt=""
              className={[
                "size-[21px] object-contain opacity-90",
                isActive
                  ? "brightness-0 saturate-100 invert-[27%] sepia-[96%] saturate-[3814%] hue-rotate-[214deg] brightness-[99%] contrast-[103%]"
                  : "",
              ].join(" ")}
            />
          ) : (
            <span className="flex size-[21px] items-center justify-center [&>svg]:size-5">
              {icon}
            </span>
          )}
        </span>
        <span className="min-w-0 truncate font-sf-medium">{label}</span>
      </a>
    </li>
  );
};

export default SideMenu;
